import { useMemo } from 'react'
import useApiService from 'services/api'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { getTargetType } from './targetTypeMap'
import { adaptUnifiedListParams } from './unifiedResponseAdapter'
import {
  adaptUnifiedDoorItemForVertical,
  DoorVerticalRowConfig,
} from './unifiedDoorResponseAdapter'

/**
 * Flag-gated data-source swap for an opportunity-DOORS target table (FIR-6995).
 *
 * The door mirror of `useUnifiedTableActions`. Returns `{ listAction,
 * summaryAction }` bound to a door-bearing vertical. When the
 * `enable_unified_opps_endpoint` flag is ON (reusing the SAME flag as the opps
 * swap — no new flag), the actions hit the unified `/opportunities/doors/`
 * endpoint (resolving the vertical through `getTargetType`, shaping outgoing
 * params via `adaptUnifiedListParams`, and re-nesting each row via
 * `adaptUnifiedDoorItemForVertical` so the legacy door columns/links/actions
 * resolve). When OFF, the caller's existing legacy actions are returned
 * untouched, so the flag-off path is byte-for-byte unchanged.
 *
 * Door-summary mapping: the door stat card / select-all total reads
 * `summaryRes.alt_count` (NOT `count`). The legacy door summary set
 * `alt_count = distinct parent-chain count`
 * (`Count("chain_proxy_id", distinct=True)`), while `count` = DOOR count. The
 * unified doors summary now returns both fields with the same semantics
 * (`alt_count` = distinct `contact_company_id`, the chain-proxy FK), so this
 * adapter passes them through unchanged — restoring the legacy "N chains"
 * header stat under the flag.
 *
 * @param vertical app vertical / sub-category slug (routed through `getTargetType`)
 * @param rowConfig how to re-nest the flat unified door row for this vertical
 * @param legacy the existing legacy list/summary actions (used when flag is off)
 */
export function useUnifiedDoorsTableActions<T extends { id: number }>(
  vertical: string,
  rowConfig: DoorVerticalRowConfig,
  legacy: {
    listAction: (
      params: Record<string, any>,
      signal?: AbortSignal
    ) => Promise<{ results: T[] }>
    summaryAction: (
      params: Record<string, any>,
      signal?: AbortSignal
    ) => Promise<{ count: number; alt_count: number }>
  }
): {
  listAction: (
    params: Record<string, any>,
    signal?: AbortSignal
  ) => Promise<{ results: T[] }>
  summaryAction: (
    params: Record<string, any>,
    signal?: AbortSignal
  ) => Promise<{ count: number; alt_count: number }>
} {
  const api = useApiService()
  const { enableUnifiedOppsEndpoint } = useFeatureFlagService()

  return useMemo(() => {
    if (!enableUnifiedOppsEndpoint) {
      return legacy
    }

    const targetType = getTargetType(vertical)

    const listAction = async (
      params: Record<string, any>,
      signal?: AbortSignal
    ) => {
      const res = await api.getOpportunityDoorsList(
        targetType,
        adaptUnifiedListParams(params, targetType),
        signal
      )
      return {
        ...res,
        results: res.results.map((item) =>
          adaptUnifiedDoorItemForVertical(
            item as Record<string, unknown>,
            rowConfig
          )
        ),
      } as unknown as { results: T[] }
    }

    const summaryAction = async (
      params: Record<string, any>,
      signal?: AbortSignal
    ) => {
      const res = await api.getOpportunityDoorsSummary(
        targetType,
        adaptUnifiedListParams(params, targetType),
        signal
      )
      // `count` = door count, `alt_count` = distinct parent-chain count (the
      // door stat-card total). The unified summary returns both with the legacy
      // semantics; pass them through.
      return { count: res.count, alt_count: res.alt_count }
    }

    return { listAction, summaryAction }
    // `legacy` action identities are stable across renders (api methods); we key
    // the swap on the flag + vertical + row config.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enableUnifiedOppsEndpoint,
    vertical,
    api,
    rowConfig.accessor,
    rowConfig.detailLinkIdField,
  ])
}
